import { Injectable, inject } from '@angular/core';
import { BillingService } from './billing.service';
import { FEATURE_PLAN_MATRIX, FREE_TIER_LIMITS, PremiumFeature } from '../constants/feature-access.constants';
import { EffectiveEntitlement } from '../models/entitlement.models';

export interface FeatureGateResult {
  allowed: boolean;
  reason?: string;
  requiredPlan?: 'complete';
}

export interface SharedEventLimitResult extends FeatureGateResult {
  limit?: number;
  current?: number;
}

@Injectable({
  providedIn: 'root'
})
export class FeatureGateService {
  private billingService = inject(BillingService);

  async refreshEntitlement(): Promise<EffectiveEntitlement> {
    return this.billingService.refreshEntitlement();
  }

  async canUse(feature: PremiumFeature): Promise<boolean> {
    const result = await this.checkFeature(feature);
    return result.allowed;
  }

  /**
   * Plan check against the last known entitlement. Returns null when nothing
   * is cached yet so callers can decide whether to hit the network.
   */
  canUseCached(feature: PremiumFeature): boolean | null {
    const entitlement = this.billingService.entitlement();
    if (!entitlement) {
      return null;
    }
    return this.evaluateEntitlement(feature, entitlement).allowed;
  }

  async checkFeature(feature: PremiumFeature): Promise<FeatureGateResult> {
    const cached = this.canUseCached(feature);
    if (cached === true) {
      return { allowed: true };
    }

    const entitlement = await this.billingService.refreshEntitlement();
    return this.evaluateEntitlement(feature, entitlement);
  }

  private evaluateEntitlement(feature: PremiumFeature, entitlement: EffectiveEntitlement): FeatureGateResult {
    if (entitlement.premiumActive) {
      return { allowed: true };
    }

    const isPremiumFeature = FEATURE_PLAN_MATRIX.complete.features.includes(feature);
    if (!isPremiumFeature) {
      return { allowed: true };
    }

    return {
      allowed: false,
      requiredPlan: 'complete',
      reason: 'This feature is part of Complete.'
    };
  }

  async checkSharedEventCreation(activeSharedEventCount: number): Promise<SharedEventLimitResult> {
    const entitlement = await this.billingService.refreshEntitlement();
    if (entitlement.premiumActive) {
      return { allowed: true };
    }

    const limit = FREE_TIER_LIMITS.maxActiveSharedEvents;
    if (activeSharedEventCount >= limit) {
      return {
        allowed: false,
        requiredPlan: 'complete',
        reason: `Free plan supports up to ${limit} active shared event${limit === 1 ? '' : 's'}.`,
        limit,
        current: activeSharedEventCount
      };
    }

    return { allowed: true, limit, current: activeSharedEventCount };
  }

  async checkSharedPeerCount(connectedPeers: number): Promise<SharedEventLimitResult> {
    const entitlement = await this.billingService.refreshEntitlement();
    if (entitlement.premiumActive) {
      return { allowed: true };
    }

    const limit = FREE_TIER_LIMITS.maxDevicesPerSharedEvent;
    if (connectedPeers >= limit) {
      return {
        allowed: false,
        requiredPlan: 'complete',
        reason: `Free plan supports up to ${limit} connected devices per shared event.`,
        limit,
        current: connectedPeers
      };
    }

    return { allowed: true, limit, current: connectedPeers };
  }
}
